import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Nav from "../components/app/Nav";
import Footer from "../components/app/Footer";
import FloatingButton from "../components/app/FloatingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faClock,
  faPaperPlane,
  faCheckCircle,
  faExclamationTriangle,
  faHeadset,
  faCommentDots,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import styles from "../styles/app/ContactPage.module.css";
import btnStyles from "../styles/components/Button.module.css";
import getApiBaseUrl from "../services/getserver.service";

const ContactPage: React.FC = () => {
  const infoEmail = process.env.REACT_APP_INFO_EMAIL;
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    telefono: "",
    mensaje: "",
  });

  const [formStatus, setFormStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus("sending");

    try {
      const payload = {
        nombre: `${formData.nombre} ${formData.apellido}`.trim(),
        email: formData.email,
        telefono: formData.telefono,
        mensaje: formData.mensaje,
      };

      const response = await fetch(`${getApiBaseUrl()}/api/contact/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormStatus("success");
        setFormData({
          nombre: "",
          apellido: "",
          email: "",
          telefono: "",
          mensaje: "",
        });
        setTimeout(() => setFormStatus("idle"), 5000);
      } else {
        setFormStatus("error");
        setTimeout(() => setFormStatus("idle"), 5000);
      }
    } catch (error) {
      console.error("Error al enviar formulario:", error);
      setFormStatus("error");
      setTimeout(() => setFormStatus("idle"), 5000);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contacto - elPaket | Envíos a Latinoamérica</title>
        <meta
          name="description"
          content="Contáctanos para enviar paquetes a Venezuela, República Dominicana, Costa Rica, Guatemala y Perú. Oficina en Doral, FL. Atención 24/7."
        />
        <meta
          name="keywords"
          content="contacto elpaket, envíos latinoamérica, dirección Doral, formulario contacto, soporte"
        />
        <link rel="canonical" href="https://elpaket.com/contacto" />
      </Helmet>

      <Nav />
      {/*
      <FloatingButton />
      */}

      <div className={styles.contactPage}>
        {/* ── Hero ── */}
        <div className={styles.contactHero}>
          <div className={styles.contactHeroContent}>
            <div className={styles.contactHeroLeft}>
              <p className={styles.headerBrand}>elPaket</p>
              <h1 className={styles.contactHeroTitle}>Soporte</h1>
              <p className={styles.contactSubtitleHero}>
                Estamos aquí para ayudarte con tus envíos.
              </p>
            </div>
            <div className={styles.contactHeroRight}>
              <FontAwesomeIcon
                icon={faHeadset}
                className={styles.contactHeroIcon}
              />
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className={styles.contactBody}>
          <div className={styles.contactContainer}>
            {/* Columna izquierda: formulario */}
            <div className={styles.contactFormCard}>
              <div className={styles.contactFormCardHeader}>
                <div className={styles.contactFormCardHeaderIcon}>
                  <FontAwesomeIcon icon={faCommentDots} />
                </div>
                <h2 className={styles.contactFormCardTitle}>Escríbenos</h2>
              </div>
              <p className={styles.formSubtitle}>
                Te responderemos en menos de 24 horas
              </p>

              <form className={styles.contactForm} onSubmit={handleSubmit}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="nombre">Nombre Completo *</label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                      placeholder="Ej: Juan Pérez"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="email">Correo Electrónico *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                      placeholder="Ej: tu@email.com"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="telefono">Teléfono</label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    autoComplete="off"
                    placeholder="Ej: +1 (305) 123-4567"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="mensaje">Mensaje *</label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Cuéntanos cómo podemos ayudarte..."
                  />
                </div>

                <button
                  type="submit"
                  className={btnStyles.primary}
                  disabled={formStatus === "sending"}
                >
                  {formStatus === "sending" ? (
                    "Enviando..."
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} /> Enviar mensaje
                    </>
                  )}
                </button>

                {formStatus === "success" && (
                  <div
                    className={`${styles.formAlert} ${styles.formAlertSuccess}`}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} /> Mensaje enviado
                    correctamente. Te contactaremos pronto.
                  </div>
                )}

                {formStatus === "error" && (
                  <div
                    className={`${styles.formAlert} ${styles.formAlertError}`}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} /> Hubo un
                    error. Intenta nuevamente o contáctanos por WhatsApp.
                  </div>
                )}
              </form>
            </div>

            {/* Columna derecha: info cards */}
            <div className={styles.contactInfoSide}>
              <div className={styles.infoCard}>
                <div className={styles.infoCardIcon}>
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <div>
                  <strong>Correo Electrónico</strong>
                  <a href="mailto:soporte@enviarlatam.com">soporte@enviarlatam.com</a>
                  <span>Te respondemos en menos de 24 horas.</span>
                </div>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.infoCardIcon}>
                  <FontAwesomeIcon icon={faPhone} />
                </div>
                <div>
                  <strong>Teléfono</strong>
                  <a
                    href={`tel:${process.env.REACT_APP_WHATSAPP_NUMBER || "17868380122"}`}
                  >
                    {process.env.REACT_APP_CONTACT_PHONE || "+1 786-838-0122"}
                  </a>
                  <span>Lunes a Viernes: 8AM – 5PM (EST)</span>
                </div>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.infoCardIcon}>
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div>
                  <strong>Horarios de Atención</strong>
                  <span>Lunes a Viernes</span>
                  <span>8AM – 5PM (EST)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ContactPage;
